import { z } from 'zod';
import { SOURCES_OF_FUNDS } from './types';

const csvBool = z.preprocess((v) => {
  if (typeof v === 'boolean') return v;
  if (typeof v === 'string') {
    const t = v.trim().toUpperCase();
    if (t === 'TRUE') return true;
    if (t === 'FALSE' || t === '') return false;
  }
  return v;
}, z.boolean());

const optionalDate = z
  .union([z.string().regex(/^\d{4}-\d{2}-\d{2}$/), z.literal(''), z.null()])
  .transform((v) => (v ? v : null));

/**
 * Schema for one row of the provided CSV. Intentionally permissive on stored
 * `risk_classification` and `kyc_status` (they may be blank) because the CSV
 * is dirty by design — the audit view surfaces those gaps as findings.
 */
export const clientRecordSchema = z.object({
  client_id: z.string().min(1).max(32),
  branch: z.string().min(1).max(64),
  onboarding_date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  client_name: z.string().min(1).max(120),
  client_type: z.enum(['INDIVIDUAL', 'ENTITY']),
  country_of_tax_residence: z.string().min(1).max(64),
  annual_income: z.coerce.number().int().nonnegative().max(1_000_000_000_000),
  source_of_funds: z.string().min(1).max(64),
  pep_status: csvBool,
  sanctions_screening_match: csvBool,
  adverse_media_flag: csvBool,
  risk_classification: z.enum(['LOW', 'MEDIUM', 'HIGH', '']).default(''),
  kyc_status: z
    .enum(['APPROVED', 'PENDING', 'REJECTED', 'ENHANCED_DUE_DILIGENCE', ''])
    .default(''),
  id_verification_date: optionalDate,
  relationship_manager: z.string().max(64).default(''),
  documentation_complete: csvBool,
});

export const rulesConfigSchema = z.object({
  version: z.string().min(1),
  description: z.string().optional(),
  high: z.object({
    booleanFlags: z.array(
      z.enum(['pep_status', 'sanctions_screening_match', 'adverse_media_flag']),
    ),
    countries: z.array(z.string().min(1)),
  }),
  medium: z.object({
    clientTypes: z.array(z.enum(['INDIVIDUAL', 'ENTITY'])),
    countries: z.array(z.string().min(1)),
    incomeThreshold: z.number().nonnegative(),
    sourcesOfFunds: z.array(z.string().min(1)),
  }),
});

/**
 * Form schema for new intake. Stricter than the CSV schema because the
 * RM is recording the assessment at point of intake — every field is required.
 */
export const intakeFormSchema = z.object({
  client_name: z
    .string()
    .trim()
    .min(2, 'Client name is required')
    .max(120, 'Client name is too long'),
  client_type: z.enum(['INDIVIDUAL', 'ENTITY'], {
    errorMap: () => ({ message: 'Select INDIVIDUAL or ENTITY' }),
  }),
  branch: z.string().min(1, 'Branch is required').max(64),
  country_of_tax_residence: z
    .string()
    .trim()
    .min(1, 'Country of tax residence is required')
    .max(64),
  annual_income: z.coerce
    .number({ invalid_type_error: 'Annual income must be a number' })
    .int('Annual income must be a whole number')
    .nonnegative('Annual income cannot be negative')
    .max(1_000_000_000, 'Annual income value is unrealistic'),
  source_of_funds: z.enum(SOURCES_OF_FUNDS, {
    errorMap: () => ({ message: 'Select a source of funds' }),
  }),
  pep_status: z.boolean(),
  sanctions_screening_match: z.boolean(),
  adverse_media_flag: z.boolean(),
  documentation_complete: z.boolean(),
  id_verification_date: z
    .string()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'Enter a valid date')
    .refine((d) => new Date(d) <= new Date(), { message: 'Date cannot be in the future' }),
  relationship_manager: z.string().min(1, 'RM is required').max(64),
});

export type IntakeFormValues = z.infer<typeof intakeFormSchema>;
